/**
 * QR Print Utilities
 *
 * Shared print HTML generation for bin, OOS, and shelf QR codes.
 * Produces a consistent 70mm QR layout, one per A4 page.
 */

/** Allowed QR label types for print layout. (BLU-197: BENCH for work benches;
 *  FILM_SLOT: DTF film-storage pigeon holes + shelves.) */
export type QRBinType = 'REGULAR' | 'OOS' | 'SHELF' | 'BENCH' | 'FILM_SLOT';

export interface BinQRData {
  /** Display label (e.g., "BIN-001", "PACKING SHELF") */
  label: string;
  /** Raw QR content string (e.g., "THP://BIN:BIN-001"). Optional — not used in print output. */
  qrContent?: string;
  /** Base64 data URL of the QR code PNG image */
  qrDataUrl: string;
}

/**
 * HTML-escape user-provided strings to prevent XSS in print windows.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Generate print-ready HTML for QR codes.
 * Opens in a new window for clean printing without dialog interference.
 */
export function generatePrintHTML(qrCodes: BinQRData[], binType: QRBinType, typeLabel: string): string {
  const qrItems = qrCodes.map(qr => `
    <div class="qr-page">
      <img src="${escapeHtml(qr.qrDataUrl)}" alt="QR code for ${escapeHtml(qr.label)}" class="qr-image" />
      <div class="qr-label">${escapeHtml(qr.label)}</div>
      <div class="qr-type">${escapeHtml(typeLabel)}</div>
    </div>
  `).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <title>${escapeHtml(binType)} QR Codes - ThreadHeads</title>
  <meta charset="UTF-8">
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .qr-page {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .qr-image {
      width: 70mm;
      height: 70mm;
      image-rendering: pixelated;
    }

    .qr-label {
      font-size: 28px;
      font-weight: 700;
      margin-top: 16px;
      text-align: center;
    }

    .qr-type {
      font-size: 14px;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      margin-top: 8px;
    }

    @media print {
      @page {
        margin: 10mm;
        size: A4;
      }

      body {
        background: white;
        padding: 0;
      }

      /* One QR code per page, centered */
      .qr-page {
        height: 100vh;
        page-break-after: always;
        break-after: page;
        page-break-inside: avoid;
        break-inside: avoid;
        padding: 0;
        border: none;
      }

      .qr-page:last-child {
        page-break-after: avoid;
        break-after: avoid;
      }

      .qr-image {
        width: 70mm;
        height: 70mm;
      }

      .qr-label {
        font-size: 24px;
      }

      .qr-type {
        font-size: 16px;
        color: #333;
      }
    }

    /* Screen preview styles */
    @media screen {
      body {
        background: #f3f4f6;
        padding: 20px;
      }

      .qr-page {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        margin-bottom: 20px;
        min-height: 400px;
        padding: 40px;
      }
    }
  </style>
</head>
<body>
  ${qrItems}
</body>
</html>`;
}
